import { writable } from 'svelte/store';
import collect from 'collect.js';
import config from '@/config';
import { mergeOrAddModelDataInCollectionStore } from '@/Stores/StoreHelpers';
import DerivedMarketGroup from '@/Models/DerivedMarketGroup.js';

/**
 * Our Store can be used 2 ways in svelte components
 * 1) ($ prefix) $StoreName to reference writable store collection with all collect.js methods available
 * 2) (Direct) StoreName.method() to call a method on the store
 */
const init = () => {
	const { subscribe, set, update } = writable(collect([]));

	const methods = {
		getDerivedMarketGroupsForSport(sportId) {
			if (!config.SPORT_ENABLED) return;

			const derivedMarketGroup = new DerivedMarketGroup();
			let results = derivedMarketGroup.getBySportId(sportId);
			return results
				.then((result) => {
					set(collect(result.data));
				})
				.catch((e) => {
					console.error(e);
				});
		},

		getDerivedMarketGroup(derivedMarketGroupId) {
			if (!config.SPORT_ENABLED) return;

			const derivedMarketGroup = new DerivedMarketGroup();

			return derivedMarketGroup
				.with([])
				.find(derivedMarketGroupId)
				.then((result) => {
					return result;
				});
		},

		newDerivedMarketGroup(sport) {
			// Look for an existing "new" group that never got persisted and wipe whatever data it has
			let foundExisting = false;
			update((derivedMarketGroups) => {
				derivedMarketGroups = derivedMarketGroups.transform((derivedMarketGroup) => {
					if (derivedMarketGroup.id == 'create') {
						derivedMarketGroup.name = '';
						derivedMarketGroup.sport = sport;
						derivedMarketGroup.market_types = [];
						derivedMarketGroup.derived_market_templates = { data: collect([]) };
						foundExisting = true;
					}
					return derivedMarketGroup;
				});
				return derivedMarketGroups;
			});
			// Assuming there isn't one already existing create a new one
			if (!foundExisting) {
				let derivedMarketGroup = new DerivedMarketGroup();
				derivedMarketGroup = derivedMarketGroup.hydrate({
					id: 'create',
					name: '',
					sport: sport,
					market_types: []
				});
				derivedMarketGroup.market_types = [];
				derivedMarketGroup.derived_market_templates = { data: collect([]) };
				mergeOrAddModelDataInCollectionStore(derivedMarketGroup, this);
			}
		},

		createDerivedMarketGroup(sportId, derivedMarketGroupData) {
			const derivedMarketGroup = new DerivedMarketGroup();

			return derivedMarketGroup
				.makePersistRequest({
					url: derivedMarketGroup.resourceUrl(),
					method: 'POST',
					data: derivedMarketGroupData
				})
				.then((result) => {
					mergeOrAddModelDataInCollectionStore(result, this);
					this.getDerivedMarketGroupsForSport(sportId);
					return result;
				});
		},
		updateDerivedMarketGroup(derivedMarketGroup, overrideFormData = {}) {
			return derivedMarketGroup
				.makePersistRequest({
					url: derivedMarketGroup.selfUrl(),
					method: 'PATCH',
					data: derivedMarketGroup.preparePayload(overrideFormData)
				})
				.then((result) => {
					this.getDerivedMarketGroupsForSport(derivedMarketGroup.sport.id);
					return result;
				});
		}
	};

	return {
		subscribe,
		update,
		set,
		...methods
	};
};

export default init();
